import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { FaDownload } from 'react-icons/fa';
import { createChatSocket } from '../../utils/chatSocket';
import { graphqlRequest } from '../../utils/graphqlRequest';

const LIVE_CHAT_ACCESS_QUERY = `
  query GetLiveChatAccess {
    getMyProfile {
      company {
        liveChatEnabled
        liveChatSupporterId
      }
    }
  }
`;

const isManagementRole = (roles = []) => {
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => {
    const normalized = String(role).toLowerCase().replace(/[\s_/-]+/g, '');
    return (
      normalized.includes('admin') ||
      normalized.includes('manager') ||
      normalized.includes('supervisor') ||
      normalized.includes('operationsmanager')
    );
  });
};

const computePortalRole = (roles = [], jobTitle = '') => {
  const jt = String(jobTitle || '').toLowerCase();
  if (jt.includes('contractor')) return 'contractor';

  const normalized = (Array.isArray(roles) ? roles : []).map((r) => String(r || '').trim());
  if (normalized.some((r) => r === 'Scheduler/Dispatcher')) return 'contractor';
  if (normalized.some((r) => ['Manager', 'Operations Manager', 'Supervisor'].includes(r)))
    return 'manager';
  if (normalized.some((r) => r === 'Field Cleaner')) return 'cleaner';
  return 'contractor';
};

const roleBadgeClass = (role) => {
  if (role === 'cleaner') return 'smgmt-badge smgmt-badge--cleaner';
  if (role === 'contractor') return 'smgmt-badge smgmt-badge--contractor';
  return 'smgmt-badge smgmt-badge--manager';
};

const roleLabel = (role) => {
  if (role === 'cleaner') return 'Cleaner';
  if (role === 'contractor') return 'Contractor';
  return 'Manager';
};

const statusBadge = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'closed' || s === 'resolved') {
    return <span className="smgmt-badge smgmt-badge--resolved">Closed</span>;
  }
  return <span className="smgmt-badge smgmt-badge--new">Active</span>;
};

const formatRequestTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const safeName = (member) =>
  `${member?.firstName || ''} ${member?.lastName || ''}`.trim() || member?.email || 'User';

export default function SupportLiveChatRequests() {
  const token = Cookies.get('accessToken');
  const socketRef = useRef(null);
  const currentUserIdRef = useRef('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!token) {
      currentUserIdRef.current = '';
      return;
    }
    try {
      const decoded = jwtDecode(token);
      currentUserIdRef.current = decoded?.sub?.userId || '';
    } catch {
      currentUserIdRef.current = '';
    }
  }, [token]);

  const checkAccess = useCallback(async () => {
    if (!token) {
      return false;
    }
    let userId = '';
    try {
      const decoded = jwtDecode(token);
      userId = String(decoded?.sub?.userId || '').trim();
    } catch {
      return false;
    }
    if (!userId) {
      return false;
    }
    try {
      const data = await graphqlRequest(LIVE_CHAT_ACCESS_QUERY);
      const company = data?.getMyProfile?.company;
      const enabled = Boolean(company?.liveChatEnabled);
      const supporterId = String(company?.liveChatSupporterId || '').trim();
      const ok =
        enabled &&
        supporterId.length > 0 &&
        supporterId.toLowerCase() === userId.toLowerCase();
      return ok;
    } catch {
      return false;
    }
  }, [token]);

  const mapConversationsToRows = useCallback((conversations) => {
    const currentUserId = currentUserIdRef.current;
    const result = (Array.isArray(conversations) ? conversations : [])
      .map((conv) => {
        if (!conv || conv.type !== 'DIRECT') return null;
        const members = Array.isArray(conv.members) ? conv.members : [];
        const partner = members.find((m) => m?.id && m.id !== currentUserId);
        if (!partner?.id) return null;
        if (isManagementRole(partner.roles || [])) return null;

        const role = computePortalRole(partner.roles || [], partner.jobTitle || '');
        const requestAt = conv.lastMessageAt || conv.createdAt || null;
        const branchName = conv.branchName || conv.projectName || conv.branch || '—';
        const status = conv.status || (conv.closedAt ? 'closed' : 'active');
        const assignedAdmin = conv.assignedAdminName || '—';

        return {
          conversationId: conv.conversationId,
          userName: safeName(partner),
          userRole: role,
          branchName,
          requestAt,
          status,
          assignedAdmin,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.requestAt || 0).getTime() - new Date(a.requestAt || 0).getTime());

    return result.map((row, idx) => ({
      ...row,
      chatId: `CHAT-${String(idx + 1).padStart(3, '0')}`,
    }));
  }, []);

  const load = useCallback(() => {
    if (!socketRef.current) return;
    setError('');
    socketRef.current.emit('getMyConversations', {}, (response = {}) => {
      const conversations = Array.isArray(response?.conversations) ? response.conversations : [];
      setRows(mapConversationsToRows(conversations));
      setLoading(false);
    });
  }, [mapConversationsToRows]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');

      const ok = await checkAccess();
      if (!mounted) return;

      if (!ok) {
        setLoading(false);
        setRows([]);
        setError(token ? 'Live chat is not enabled for this admin.' : 'Please log in to view live chat requests.');
        return;
      }

      const socket = createChatSocket(token);
      socketRef.current = socket;

      socket.on('connect', () => {
        setError('');
        load();
      });
      socket.on('disconnect', () => {
        // keep last data
      });
      socket.on('connect_error', (e) => {
        setError(e?.message || 'Live chat unavailable');
        setLoading(false);
      });
      socket.on('conversation.created', () => load());
      socket.on('notification.message', () => load());

      // initial load even if connect callback is delayed
      load();
    })();

    return () => {
      mounted = false;
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [checkAccess, load, token]);

  const onViewChat = useCallback((conversationId) => {
    if (!conversationId) return;
    window.dispatchEvent(
      new CustomEvent('admin-live-chat-open', { detail: { conversationId } }),
    );
  }, []);

  const exportCsv = useCallback(() => {
    const header = [
      'Chat ID',
      'User Name',
      'User Role',
      'Branch Name',
      'Request Time',
      'Status',
      'Assigned Admin',
    ];
    const lines = [
      header.join(','),
      ...rows.map((r) =>
        [
          r.chatId,
          r.userName,
          roleLabel(r.userRole),
          r.branchName,
          formatRequestTime(r.requestAt),
          String(r.status || ''),
          r.assignedAdmin,
        ]
          .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `live-chat-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const countLabel = useMemo(() => {
    if (loading) return 'Loading…';
    return `Showing ${rows.length} chat request${rows.length === 1 ? '' : 's'}`;
  }, [loading, rows.length]);

  return (
    <div className="support-live-chat-requests">
      <div className="smgmt-page-head">
        <h1>Live Chat Requests</h1>
        <p>Manage real-time support chat requests from users</p>
      </div>

      <div className="smgmt-card">
        <div className="smgmt-table-toolbar">
          <div>
            <h2>Chat Requests</h2>
            <p className="smgmt-muted">{countLabel}</p>
          </div>
          <button type="button" className="support-tickets-page__export" onClick={exportCsv} disabled={rows.length === 0}>
            <FaDownload aria-hidden />
            Export
          </button>
        </div>

        {error ? <p style={{ color: '#b91c1c', marginTop: 8 }}>{error}</p> : null}

        <div className="smgmt-table-wrap">
          <table className="smgmt-table">
            <thead>
              <tr>
                <th>Chat ID</th>
                <th>User name</th>
                <th>User role</th>
                <th>Branch name</th>
                <th>Request time</th>
                <th>Status</th>
                <th>Assigned admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="smgmt-muted">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="smgmt-muted">
                    No chat requests yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.conversationId}>
                    <td>
                      <strong>{row.chatId}</strong>
                    </td>
                    <td>{row.userName}</td>
                    <td>
                      <span className={roleBadgeClass(row.userRole)}>{roleLabel(row.userRole)}</span>
                    </td>
                    <td>{row.branchName || '—'}</td>
                    <td>{formatRequestTime(row.requestAt)}</td>
                    <td>{statusBadge(row.status)}</td>
                    <td>{row.assignedAdmin || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="smgmt-btn smgmt-btn-purple"
                        onClick={() => onViewChat(row.conversationId)}
                      >
                        View Chat
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
